import { Component, computed, OnInit, signal } from "@angular/core";
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { ActivatedRoute, Router } from "@angular/router";
import { ImageUrlDirective } from "@app/directives";
import {
  FormAutocompleteComponent,
  FormAutocompleteOption,
} from "@components/filters/form-autocomplete/form-autocomplete.component";
import { environment } from "@environments/environment";
import { RequestService } from "@services/index";

type CategoryType = "category" | "sub-category" | "sub-sub-category";

@Component({
  selector: "fh-category-input",
  templateUrl: "./category-input.component.html",
  styleUrls: ["./category-input.component.css"],
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FormAutocompleteComponent,
    MatButtonModule,
    MatRadioModule,
    ImageUrlDirective,
  ],
})
export class CategoryInputComponent implements OnInit {
  error = "";
  success = "";
  errorUpload = "";
  successUpload = "";

  isSubmitted = false;
  selectedCatId = 0;
  button_disabled = false;

  category_img_url: any;
  categoryImageBase64: string | ArrayBuffer | null = null;
  image_err_msg = "";
  image_ext = "";
  image_submit = false;
  app_url = environment.backendURL;

  is_featured_err_msg = "";

  // Category hierarchy data
  mainCategories = signal<any[]>([]);
  subCategories = signal<any[]>([]);
  categoryType = signal<CategoryType>("category");

  // Computed options for dropdowns
  mainCategoryOptions = computed<FormAutocompleteOption<number>[]>(() =>
    this.mainCategories().map((c) => ({ value: c.id, label: c.category_name }))
  );

  subCategoryOptions = computed<FormAutocompleteOption<number>[]>(() =>
    this.subCategories().map((c) => ({ value: c.id, label: c.category_name }))
  );

  isFeaturedOptions: FormAutocompleteOption<string>[] = [
    { value: "No", label: "No" },
    { value: "Yes", label: "Yes" },
  ];

  categoryForm = this.formBuilder.group({
    category_name: ["", Validators.required],
    main_category_id: [null as number | null],
    sub_category_id: [null as number | null],
    image: [""],
    is_featured: ["No"],
  });

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private auth: RequestService
  ) {
    this.selectedCatId = parseInt(this.route.snapshot.paramMap.get("id")!, 10);
  }

  get category_name() {
    return this.categoryForm.get("category_name");
  }

  get image() {
    return this.categoryForm.get("image");
  }

  get is_featured() {
    return this.categoryForm.get("is_featured");
  }

  onCategoryTypeChange(type: CategoryType): void {
    this.categoryType.set(type);
    // Clear parent selections when changing type
    this.categoryForm.patchValue({
      main_category_id: null,
      sub_category_id: null,
    });
    this.subCategories.set([]);
  }

  onMainCategoryChange(mainCategoryId: number | null): void {
    if (mainCategoryId && this.categoryType() === "sub-sub-category") {
      // Load sub-categories for the selected main category
      this.loadSubCategories(mainCategoryId);
    } else {
      this.subCategories.set([]);
      this.categoryForm.patchValue({ sub_category_id: null });
    }
  }

  loadSubCategories(mainCategoryId: number): void {
    // Using new v2 API with parent_id filter and status=active
    this.auth.get(`/api/v2/categories?parent_id=${mainCategoryId}&status=active`).subscribe({
      next: (res: any) => {
        if (res.status === true) {
          this.subCategories.set(res.data?.categories || []);
        }
      },
      error: (error) => {
        console.error("Error loading sub-categories:", error);
        this.subCategories.set([]);
      },
    });
  }

  uploadCategoryPicture(fileInput: HTMLInputElement) {
    if (!fileInput.files || fileInput.files.length === 0) {
      return;
    }
    const event = { target: fileInput };
    this.onFileInput(event);
  }

  ngOnInit(): void {
    this.loadMainCategories();

    if (this.selectedCatId !== 0) {
      this.loadCategoryDetails();
    }
  }

  loadMainCategories(): void {
    // Using new v2 API with parent_id=0 filter for main categories
    this.auth.get("/api/v2/categories?parent_id=0&status=active").subscribe({
      next: (res: any) => {
        if (res.status === true) {
          this.mainCategories.set(res.data?.categories || []);
        }
      },
      error: (error) => {
        console.error("Error loading main categories:", error);
      },
    });
  }

  loadCategoryDetails(): void {
    // Using new v2 API
    this.auth.get(`/api/v2/categories/${this.selectedCatId}`).subscribe({
      next: (res: any) => {
        if (res.result_code === 1 || res.status === true) {
          const data = res.data;

          this.categoryForm.patchValue({
            category_name: data.category_name,
            is_featured: data.is_featured || "No",
          });

          // Determine category type based on parent_parent_id and parent_id
          if (data.parent_parent_id && data.parent_parent_id !== 0) {
            // This is a sub-sub-category
            this.categoryType.set("sub-sub-category");
            this.categoryForm.patchValue({
              main_category_id: data.parent_parent_id,
              sub_category_id: data.parent_category_id,
            });
            // Load sub-categories for the main category
            if (data.parent_parent_id) {
              this.loadSubCategories(data.parent_parent_id);
            }
          } else if (data.parent_id && data.parent_id !== 0) {
            // This is a sub-category
            this.categoryType.set("sub-category");
            this.categoryForm.patchValue({
              main_category_id: data.parent_id,
            });
          } else {
            // This is a main category
            this.categoryType.set("category");
          }

          if (data.category_img) {
            this.image_submit = true;
            this.category_img_url = data.category_img;
            this.image_err_msg = "";
          }
        } else {
          console.error("Failed to load category:", res);
          this.error = res.message || "Failed to load category details";
        }
      },
      error: (err) => {
        console.error("Error loading category:", err);
        this.error = "Error loading category details";
      },
    });
  }

  save(): void {
    this.isSubmitted = true;
    if (!this.categoryForm.valid) {
      return;
    }

    // Validate based on category type
    const type = this.categoryType();
    const mainCategoryId = this.categoryForm.value.main_category_id;
    const subCategoryId = this.categoryForm.value.sub_category_id;

    if (type === "sub-category" && !mainCategoryId) {
      this.error = "Please select a main category";
      return;
    }

    if (type === "sub-sub-category" && (!mainCategoryId || !subCategoryId)) {
      this.error = "Please select both main category and sub-category";
      return;
    }

    if (
      (!this.category_img_url || this.image_submit === false) &&
      this.categoryForm.value.is_featured === "Yes"
    ) {
      this.image_err_msg = "Please choose an image";
      return;
    } else {
      this.image_err_msg = "";
    }

    this.button_disabled = true;

    // Determine parent_id based on category type
    let parent_id = 0;
    if (type === "sub-category") {
      parent_id = mainCategoryId || 0;
    } else if (type === "sub-sub-category") {
      parent_id = subCategoryId || 0;
    }

    const data = {
      category_name: this.categoryForm.value.category_name,
      image: this.categoryForm.value.image,
      is_featured: this.categoryForm.value.is_featured,
      parent_id: parent_id,
      image_ext: this.image_ext,
    };

    // Using new v2 API endpoints
    if (this.selectedCatId) {
      this.auth.put(`/api/v2/categories/${this.selectedCatId}`, data).subscribe({
        next: (res: any) => {
          if (res.result_code === 1 || res.status === true) {
            this.router.navigate(["/categories"], {
              state: { success_msg: res.message || "Category updated successfully." },
            });
          } else {
            this.error = res.message;
            this.button_disabled = false;
          }
        },
        error: (err) => {
          this.error = err.error?.message || "Error updating category";
          this.button_disabled = false;
        },
      });
    } else {
      this.auth.post("/api/v2/categories", data).subscribe({
        next: (res: any) => {
          if (res.result_code === 1 || res.status === true) {
            this.router.navigate(["/categories"], {
              state: { success_msg: res.message || "Category created successfully." },
            });
          } else {
            this.error = res.message;
            this.button_disabled = false;
          }
        },
        error: (err) => {
          this.error = err.error?.message || "Error creating category";
          this.button_disabled = false;
        },
      });
    }
  }

  onFileInput(event: any) {
    const reader = new FileReader();
    const len = event.target.files[0].name.split(".").length;
    const ext = event.target.files[0].name.split(".")[len - 1];
    this.image_ext = ext;
    if (
      ext === "png" ||
      ext === "PNG" ||
      ext === "jpg" ||
      ext === "JPG" ||
      ext === "jpeg" ||
      ext === "JPEG"
    ) {
      reader.readAsDataURL(event.target.files[0]);
      reader.onload = () => {
        this.categoryForm.patchValue({ image: reader.result });
        this.categoryImageBase64 = reader.result;
        this.image_submit = true;
        this.image_err_msg = "";
      };
    } else {
      this.image_err_msg = "File format is not valid.";
    }
  }
}
